import * as t from 'io-ts';

import type {
    SelectCurrentViewDefinition,
    InteractiveTitleSettingDefinition,
    InteractiveTitleSelectCurrentViewDefinition,
} from 'header/types';
import { ViewHelper } from 'portlet/models/viewHelper';

import { define, createSpecSettingsInterface } from './_common';

export interface CurrentViewSettingDefinition extends SelectCurrentViewDefinition {
    key: 'VIEWID';
}

const definition: CurrentViewSettingDefinition = {
    type: 'SelectCurrentView',
    key: 'VIEWID',
    label: 'View',
    options: [],
    value: 1,
};

// note: current setting value is never read from spec.settings, therefore we leave it as unknown
const specSettingsInterface = createSpecSettingsInterface(definition, t.unknown);

export const codec = specSettingsInterface.input;

export const CurrentViewSettingDefinition = define({
    key: 'VIEWID',
    label: 'viewId',
    definition,
    fromSetting: (_, spec, view) => (view ? view.id : spec.defaultViewId),
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: (_, spec): CurrentViewSettingDefinition => {
        const options = ViewHelper.getViewGroups(spec);
        return {
            ...definition,
            options,
            scope: options.flatMap(({ members }) => members).length < 2 ? 'none' : 'user',
        };
    },
    applySettingDependency: ({ diff, settings, specification }) => {
        if (!diff.VIEW_FORM || !diff.VIEW_FORM.current || !settings.VIEWID) return;

        const currentViewForm = diff.VIEW_FORM.current;
        const currentView = ViewHelper.getViewById(specification, settings.VIEWID);

        if (!currentView) return;

        // search for a view within the same group but with matching view form
        const nextView = ViewHelper.getViewGroup(specification, currentView).members.find(
            ({ form }) => form.toLowerCase() === currentViewForm.toLowerCase(),
        );

        return nextView ? { value: nextView.id } : undefined;
    },
    interactiveTags: definition => {
        const viewId: InteractiveTitleSettingDefinition<
            CurrentViewSettingDefinition,
            InteractiveTitleSelectCurrentViewDefinition
        > = {
            setting: definition,
            field: {
                ...definition,
            },
            format: value => ({ ...definition, value }),
        };

        const view: InteractiveTitleSettingDefinition<
            CurrentViewSettingDefinition,
            InteractiveTitleSelectCurrentViewDefinition
        > = {
            setting: definition,
            field: definition,
            format: value => ({ ...definition, value }),
        };

        return {
            VIEWID: viewId,
            VIEW: view,
        };
    },
    replacementTags: definition => ({
        VIEWID: definition.value.toString(),
        VIEW:
            definition.options
                .flatMap(({ members }) => members)
                .find(({ id }) => id === definition.value)?.label || '',
    }),
});
